import { TMDbMovie } from './tmdbApi';
import {
  UserProfile,
  isFirebaseInitialized,
  serverTimestamp,
  unifiedGetDoc,
  unifiedSetDoc
} from '@watchlist/shared';

export interface RecentItem {
  id: string;
  timestamp: number;
  data?: any;
}

export interface RecentMovieItem extends RecentItem {
  data: TMDbMovie;
}

export interface RecentWatchlistItem extends RecentItem {
  data: {
    name: string;
    movieCount: number;
    createdBy: string;
    movieDetails?: any[];
    visibility?: string;
    collaborators?: string[];
  };
}

export interface RecentUserItem extends RecentItem {
  data: UserProfile;
}

export interface RecentPersonItem extends RecentItem {
  data: {
    name: string;
    known_for_department: string;
    profile_path?: string;
  };
}

class RecentActivityService {
  private readonly MAX_ITEMS = 20;
  private cache: Map<string, any[]> = new Map();
  private watchlistMetaCache: Map<string, any> = new Map();

  private async getWatchlistMeta(watchlistId: string): Promise<any | null> {
    if (!watchlistId) {
      return null;
    }

    if (this.watchlistMetaCache.has(watchlistId)) {
      return this.watchlistMetaCache.get(watchlistId);
    }

    try {
      if (!isFirebaseInitialized()) {
        return null;
      }

      // Use unified service for cross-platform support
      const watchlistSnap = await unifiedGetDoc('watchlists', watchlistId);
      if (!watchlistSnap || !watchlistSnap.exists || !watchlistSnap.exists()) {
        return null;
      }

      const data = watchlistSnap.data();
      this.watchlistMetaCache.set(watchlistId, data);
      return data;
    } catch (error) {
      console.error('RecentActivityService: Error fetching watchlist meta:', error);
      return null;
    }
  }

  // Get items from Firebase
  private async getItemsFromFirebase<T extends RecentItem>(
    userId: string,
    type: 'movies' | 'watchlists' | 'users' | 'people'
  ): Promise<T[]> {
    try {
      if (!isFirebaseInitialized()) {
        console.warn(`📍 Firebase not initialized, cannot get recent ${type}`);
        return [];
      }

      // Brief safety delay to ensure auth state has fully propagated to Firestore
      await new Promise(resolve => setTimeout(resolve, 200));

      console.log(`📍 RecentActivityService: Getting recent ${type} for userId: ${userId}`);

      // Use unified service for cross-platform support
      const docSnap = await unifiedGetDoc(`users/${userId}/recentActivity`, type);

      if (docSnap && docSnap.exists && docSnap.exists()) {
        const data = docSnap.data();
        console.log(`✅ RecentActivityService: Retrieved ${data?.items?.length || 0} recent ${type}`);
        return (data.items || []) as T[];
      }

      console.log(`📍 RecentActivityService: No recent ${type} found for userId: ${userId}`);
      return [];
    } catch (error) {
      console.error(`❌ RecentActivityService: Error reading recent ${type} from Firebase:`, error);
      return [];
    }
  }

  // Save items to Firebase
  private async saveItemsToFirebase<T extends RecentItem>(
    userId: string,
    type: 'movies' | 'watchlists' | 'users' | 'people',
    items: T[]
  ): Promise<void> {
    try {
      if (!isFirebaseInitialized()) {
        console.warn(`📍 Firebase not initialized, cannot save recent ${type}`);
        return;
      }

      // Clean items by removing undefined values
      const cleanedItems = items.map(item => {
        const cleanedData = { ...item.data };

        // Remove undefined values from data while keeping empty strings (valid names)
        Object.keys(cleanedData).forEach(key => {
          if (cleanedData[key] === undefined) {
            delete cleanedData[key];
          }
        });

        return {
          ...item,
          data: cleanedData
        };
      });

      console.log(`📍 RecentActivityService: Saving ${cleanedItems.length} recent ${type} for userId: ${userId}`);

      // Use unified service for cross-platform support
      await unifiedSetDoc(`users/${userId}/recentActivity`, type, {
        items: cleanedItems,
        updatedAt: serverTimestamp()
      });

      console.log(`✅ RecentActivityService: Saved recent ${type} successfully`);

      // Update cache
      const cacheKey = `${userId}_${type}`;
      this.cache.set(cacheKey, items);
    } catch (error) {
      console.error(`❌ RecentActivityService: Error saving recent ${type} to Firebase:`, error);
    }
  }

  // Helper for consistent ID normalization
  private normalizeId(id: string): string {
    if (!id) return '';
    const idStr = id.toString();
    // Handles numeric IDs, tmdb_ prefix, and potential suffix like _tmdb_123
    const numericMatch = idStr.match(/(\d+)/);
    const numericId = numericMatch ? numericMatch[1] : idStr.toLowerCase();
    return `tmdb_${numericId}`;
  }

  // Add item to Firebase
  private async addItemToFirebase<T extends RecentItem>(
    userId: string,
    type: 'movies' | 'watchlists' | 'users' | 'people',
    item: T
  ): Promise<void> {
    try {
      // Get existing items
      const items = await this.getItemsFromFirebase<T>(userId, type);

      const normalizedItemId = type === 'movies' ? this.normalizeId(item.id) : item.id;

      // Collect all possible identifiers for the new item if it's a movie
      const newItemIdentifiers = new Set<string>();
      newItemIdentifiers.add(normalizedItemId);

      if (type === 'movies' && (item as any).data) {
        const movieData = (item as any).data as TMDbMovie;
        if (movieData.id) newItemIdentifiers.add(this.normalizeId(`tmdb_${movieData.id}`));
        if (movieData.imdb_id) newItemIdentifiers.add(movieData.imdb_id.toLowerCase().trim());
        if (movieData.imdbID) newItemIdentifiers.add(movieData.imdbID.toLowerCase().trim());
      }

      // Remove existing item if it matches ANY of the identifiers
      const filtered = items.filter(i => {
        const existingId = type === 'movies' ? this.normalizeId(i.id) : i.id;

        // For movies, we check for multi-ID collisions
        if (type === 'movies') {
          // If the new item's IDs contain the existing item's ID
          if (newItemIdentifiers.has(existingId)) return false;

          // Also check existing item's internal metadata for collisions
          const existingData = (i as any).data as TMDbMovie;
          if (existingData) {
            if (existingData.id && newItemIdentifiers.has(this.normalizeId(`tmdb_${existingData.id}`))) return false;
            if (existingData.imdb_id && newItemIdentifiers.has(existingData.imdb_id.toLowerCase().trim())) return false;
            if (existingData.imdbID && newItemIdentifiers.has(existingData.imdbID.toLowerCase().trim())) return false;
          }
        }

        return existingId !== normalizedItemId;
      });

      // Add new item at the beginning
      filtered.unshift(item);

      // Keep only MAX_ITEMS
      const trimmed = filtered.slice(0, this.MAX_ITEMS);

      // Save back to Firebase
      await this.saveItemsToFirebase(userId, type, trimmed);
    } catch (error) {
      console.error(`Error adding recent ${type} to Firebase:`, error);
    }
  }

  // Movies
  /**
   * Tracks a recently viewed movie
   * PRIORITIZES CANONICAL TMDB ID for the definitive record key to merge identities.
   */
  async trackMovie(movieId: string, movieData: TMDbMovie, userId?: string): Promise<void> {
    if (!userId) {
      console.warn('📍 No userId provided for tracking movie');
      return;
    }

    // Data integrity check: Don't track movies with broken/incomplete data
    const title = movieData.Title || movieData.title;
    const poster = movieData.poster_path || movieData.Poster;
    const hasPoster = poster && poster !== 'N/A';
    const isPlaceholderTitle = title === 'Movie' || (title && title.length > 20 && (title.includes('_') || title.includes('-')));

    if (!title || !hasPoster || isPlaceholderTitle) {
      console.warn('📍 Skipping tracking for broken movie data:', {
        id: movieId,
        title,
        hasPoster,
        isPlaceholder: isPlaceholderTitle
      });
      return;
    }

    // 🚀 MERGE LOGIC: Use the numeric TMDB ID as the doc ID if available.
    // This ensures that accessing via tt... or tmdb_... always points to the same doc.
    let canonicalId = movieId;
    if (movieData.id) {
      canonicalId = `tmdb_${movieData.id}`;
    } else if (movieId.startsWith('tmdb_')) {
      canonicalId = movieId;
    }

    const normalizedId = this.normalizeId(canonicalId);

    const item: RecentMovieItem = {
      id: normalizedId,
      timestamp: Date.now(),
      data: movieData
    };

    await this.addItemToFirebase<RecentMovieItem>(userId, 'movies', item);
    console.log('📍 Tracked movie to Firebase (Merged):', normalizedId, 'userId:', userId);
  }

  async getRecentMovies(userId?: string, limit: number = 6): Promise<RecentMovieItem[]> {
    if (!userId) {
      console.warn('📍 No userId provided for getting recent movies');
      return [];
    }

    // Check cache first
    const cacheKey = `${userId}_movies`;
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey) || [];
      return cached.slice(0, limit);
    }

    // Fetch from Firebase
    const items = await this.getItemsFromFirebase<RecentMovieItem>(userId, 'movies');
    console.log('📍 Getting recent movies from Firebase:', items.length, 'items for userId:', userId);
    return items.slice(0, limit);
  }

  /**
   * Deletes a specific recent item from Firebase (used for background cleanup/merging)
   */
  public async deleteRecentItem(userId: string, type: 'movies' | 'watchlists' | 'users' | 'people', itemId: string): Promise<void> {
    try {
      const items = await this.getItemsFromFirebase<RecentItem>(userId, type);
      const filtered = items.filter(i => i.id !== itemId);
      if (filtered.length !== items.length) {
        await this.saveItemsToFirebase(userId, type, filtered);
        console.log(`📍 Deleted redundant ${type} item:`, itemId);
      }
    } catch (error) {
      console.error(`❌ Error deleting recent ${type} from Firebase:`, error);
    }
  }

  // Watchlists
  async trackWatchlist(watchlistId: string, watchlistData: any, userId?: string): Promise<void> {
    if (!userId) {
      console.warn('📍 No userId provided for tracking watchlist');
      return;
    }

    const creatorId = watchlistData?.createdBy || watchlistData?.creatorUid;
    if (creatorId && creatorId === userId) {
      console.log('📍 Skipping tracking own watchlist:', watchlistId);
      return;
    }

    const watchlistName =
      watchlistData?.name ||
      watchlistData?.title ||
      watchlistData?.watchlistName ||
      'Untitled Watchlist';

    const movieDetails =
      watchlistData?.movieDetails ||
      watchlistData?.movies ||
      [];

    const collaborators =
      watchlistData?.collaborators ||
      watchlistData?.collaboratorProfiles?.map((collab: any) => collab.uid).filter(Boolean) ||
      [];

    const movieCount =
      typeof watchlistData?.movieCount === 'number'
        ? watchlistData.movieCount
        : movieDetails.length || 0;

    const isPrivate =
      typeof watchlistData?.isPrivate === 'boolean'
        ? watchlistData.isPrivate
        : watchlistData?.visibility === 'private';

    const item: RecentWatchlistItem = {
      id: watchlistId,
      timestamp: Date.now(),
      data: {
        name: watchlistName,
        movieCount,
        createdBy: creatorId,
        movieDetails: movieDetails.slice(0, 3), // Store first few movies for preview
        visibility: isPrivate ? 'private' : (watchlistData?.visibility || 'public'),
        collaborators
      }
    };

    await this.addItemToFirebase<RecentWatchlistItem>(userId, 'watchlists', item);
    console.log('📍 Tracked watchlist to Firebase:', watchlistId, 'userId:', userId);
  }

  async getRecentWatchlists(userId?: string, limit: number = 4): Promise<RecentWatchlistItem[]> {
    if (!userId) {
      console.warn('📍 No userId provided for getting recent watchlists');
      return [];
    }

    // Check cache first
    const cacheKey = `${userId}_watchlists`;
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey) || [];
      return cached.slice(0, limit);
    }

    // Fetch from Firebase
    const items = await this.getItemsFromFirebase<RecentWatchlistItem>(userId, 'watchlists');

    const enrichedItems = await Promise.all(items.map(async (item) => {
      const data = (item.data || {}) as any;
      const hasValidName = typeof data.name === 'string' && data.name.trim().length > 0;

      if (hasValidName) {
        return item;
      }

      const meta = await this.getWatchlistMeta(item.id);
      if (!meta) {
        return item;
      }

      const metaMovieDetails = Array.isArray(meta.movieDetails) ? meta.movieDetails.slice(0, 3) : [];

      return {
        ...item,
        data: {
          ...data,
          name: meta.name || meta.title || data.title || data.watchlistName || 'Watchlist',
          description: data.description || meta.description || '',
          createdBy: data.createdBy || meta.createdBy,
          movieDetails: data.movieDetails || metaMovieDetails,
          visibility: data.visibility || (meta.isPrivate ? 'private' : 'public'),
          collaborators: data.collaborators || meta.collaborators || [],
          followerCount: data.followerCount || meta.followerCount || meta.likesCount || 0
        }
      } as RecentWatchlistItem;
    }));

    return enrichedItems.slice(0, limit);
  }

  // Users
  async trackUser(userId: string, userData: UserProfile, currentUserId?: string): Promise<void> {
    if (!currentUserId) {
      console.warn('📍 No currentUserId provided for tracking user');
      return;
    }

    const item: RecentUserItem = {
      id: userId,
      timestamp: Date.now(),
      data: userData
    };

    await this.addItemToFirebase<RecentUserItem>(currentUserId, 'users', item);
    console.log('📍 Tracked user to Firebase:', userId, 'currentUserId:', currentUserId);
  }

  async getRecentUsers(userId?: string, limit: number = 4): Promise<RecentUserItem[]> {
    if (!userId) {
      console.warn('📍 No userId provided for getting recent users');
      return [];
    }

    // Check cache first
    const cacheKey = `${userId}_users`;
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey) || [];
      return cached.slice(0, limit);
    }

    // Fetch from Firebase
    const items = await this.getItemsFromFirebase<RecentUserItem>(userId, 'users');
    return items.slice(0, limit);
  }

  // People (Cast & Crew)
  async trackPerson(personId: string, personData: any, userId?: string): Promise<void> {
    if (!userId) {
      console.warn('📍 No userId provided for tracking person');
      return;
    }

    const item: RecentPersonItem = {
      id: personId,
      timestamp: Date.now(),
      data: {
        name: personData.name,
        known_for_department: personData.known_for_department,
        profile_path: personData.profile_path
      }
    };

    await this.addItemToFirebase<RecentPersonItem>(userId, 'people', item);
    console.log('📍 Tracked person to Firebase:', personId, 'userId:', userId);
  }

  async getRecentPeople(userId?: string, limit: number = 4): Promise<RecentPersonItem[]> {
    if (!userId) {
      console.warn('📍 No userId provided for getting recent people');
      return [];
    }

    // Check cache first
    const cacheKey = `${userId}_people`;
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey) || [];
      return cached.slice(0, limit);
    }

    // Fetch from Firebase
    const items = await this.getItemsFromFirebase<RecentPersonItem>(userId, 'people');
    return items.slice(0, limit);
  }

  // Clear all recent items for a user
  async clearAll(userId?: string): Promise<void> {
    if (!userId) {
      console.warn('📍 No userId provided for clearing recent items');
      return;
    }

    const types: ('movies' | 'watchlists' | 'users' | 'people')[] = ['movies', 'watchlists', 'users', 'people'];

    for (const type of types) {
      try {
        await this.saveItemsToFirebase(userId, type, []);

        // Clear cache
        const cacheKey = `${userId}_${type}`;
        this.cache.delete(cacheKey);
      } catch (error) {
        console.error(`Error clearing recent ${type}:`, error);
      }
    }
  }
}

export const recentActivityService = new RecentActivityService();
